import { Form } from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import { useStore } from "../../utils/context";
import { useNavigate } from "react-router-dom";
import useToaster from "../../components/alert";
import "../../utils/tailwind.css";
import Const from "../../utils/constant";
import { useState } from "react";
import { userLogin } from "../../api/user";
import { useMutation } from "@tanstack/react-query";
import RasaLogo from "../../assets/images/logo_blue.png";
import { Eye, EyeOff, Mail, Lock } from "lucide-react";

const LoginPage = () => {
  const { handleLogin } = useStore();
  const navigate = useNavigate();
  const toaster = useToaster();
  const [showPassword, setShowPassword] = useState(false);

  const loginQuery = useMutation({ mutationFn: userLogin });

  const validationSchema = Yup.object({
    email: Yup.string()
      .email("Please enter a valid email address.")
      .required("This field is required."),
    password: Yup.string()
      .min(6, "Password must be at least 6 characters.")
      .required("This field is required."),
  });

  const handleSubmit = async (values) => {
    try {
      const payload = {
        email: values.email,
        password: values.password,
        role: Const.USER_ROLES.ADMIN
      };
      loginQuery.mutate(payload, {
        onSuccess: (res) => {
          handleLogin(res.data);
          toaster.success(Const.MESSAGES.SUCCESS, "Logged in successfully.");
          navigate("/home");
        },
        onError: (err) => {
          const errorMsg =
            err?.response?.data?.error || Const.MESSAGES.INVALID_CREDS;
          toaster.error(Const.MESSAGES.FAILED, errorMsg);
        }
      });
    } catch (err) {
      toaster.error(Const.MESSAGES.FAILED, Const.MESSAGES.INTERNAL_SERVER_ERROR);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card m-3 ">
        <div className="login-logo mb-3 ">
          <img src={RasaLogo} alt="Rasa Enclave Logo" className="mb-2 w-30" />
          <h1>RASA Enclave</h1>
          <p>Sign in to the admin panel</p>
        </div>

        <Formik initialValues={{ email: "", password: "" }} validationSchema={validationSchema} onSubmit={handleSubmit}>
          {({ handleChange, handleBlur, handleSubmit, values, errors, touched, isSubmitting }) => (
            <Form noValidate onSubmit={handleSubmit}>
              <Form.Group controlId="formBasicEmail" style={{ marginBottom: "20px" }}>
                <Form.Label style={{ fontSize: "13px", fontWeight: 600, color: "#2c2c3e", marginBottom: "6px" }}>
                  Email Address
                </Form.Label>
                <div style={{ position: "relative" }} className="gap-2">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2" />
                  <Form.Control
                    type="email"
                    name="email"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.email && errors.email}
                    placeholder="admin@rasa.com" className="pl-10 "
                  />
                </div>
                <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
              </Form.Group>

              <Form.Group controlId="formBasicPassword" style={{ marginBottom: "28px" }}>
                <Form.Label style={{ fontSize: "13px", fontWeight: 600, color: "#2c2c3e", marginBottom: "6px" }}>
                  Password
                </Form.Label>
                <div style={{ position: "relative" }}>
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2" />
                  <Form.Control
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={values.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.password && errors.password}
                    placeholder="Enter your password"
                    className="pl-10"
                  />
                  <button
                    onClick={() => setShowPassword(!showPassword)}
                    type="button"
                    className="absolute cursor-pointer flex p-2 right-2 top-1/2 -translate-y-1/2  "
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <Form.Control.Feedback type="invalid">{errors.password}</Form.Control.Feedback>
              </Form.Group>

              <button
                type="submit"
                className="login-btn"
                disabled={loginQuery.isPending}
                style={{
                  width: "100%",
                  padding: "13px",
                  background: "linear-gradient(135deg, #1e1e5c 0%, #2a2a72 100%)",
                  border: "none",
                  borderRadius: "10px",
                  color: "#fff",
                  fontSize: "15px",
                  fontWeight: 600,
                  cursor: loginQuery.isPending ? "not-allowed" : "pointer",
                  opacity: loginQuery.isPending ? 0.7 : 1,
                  transition: "all 0.3s ease",
                }}
              >
                {loginQuery.isPending ? (
                  <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                    <span style={{
                      width: "16px",
                      height: "16px",
                      border: "2px solid rgba(255,255,255,0.3)",
                      borderTopColor: "#fff",
                      borderRadius: "50%",
                      animation: "spin 0.6s linear infinite",
                    }} />
                    Signing in...
                  </span>
                ) : "Sign In"}
              </button>

              <style>{`
                @keyframes spin {
                  to { transform: rotate(360deg); }
                }
              `}</style>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default LoginPage;
